import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Share2, Link2, Copy, Eye, Lock, CalendarClock, Trash2, Loader2, ShieldOff, Check,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

const EXPIRY = [
  { v: null, l: "Never" },
  { v: 7, l: "7 days" },
  { v: 30, l: "30 days" },
  { v: 90, l: "90 days" },
];

const TOGGLES = [
  { k: "summary", l: "Executive summary", d: "The AI summary of what was agreed." },
  { k: "decisions", l: "Decisions", d: "Decision text, owner and status." },
  { k: "actions", l: "Action items", d: "Action, owner, due date and status." },
  { k: "coverage", l: "Agenda coverage", d: "How the time was spent per agenda item." },
  { k: "participants", l: "Participant names", d: "Names only — email addresses are never shared." },
  { k: "risks", l: "Risks", d: "Internal risks raised in the meeting." },
  { k: "questions", l: "Open questions", d: "Unresolved questions and the parking lot." },
  { k: "health", l: "Meeting health", d: "Internal health score and participation stats." },
];

const DEFAULTS = {
  summary: true, decisions: true, actions: true, coverage: true,
  participants: true, risks: false, questions: false, health: false,
};

export const ShareRecap = ({ meetingId }) => {
  const [state, setState] = useState(null);
  const [vis, setVis] = useState(DEFAULTS);
  const [expiry, setExpiry] = useState(30);
  const [usePasscode, setUsePasscode] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    api
      .get(`/meetings/${meetingId}/share`)
      .then(({ data }) => {
        setState(data);
        setVis({ ...DEFAULTS, ...(data.visibility || {}) });
        if (data.shared) setUsePasscode(!!data.has_passcode);
      })
      .catch(() => setState({ shared: false }));
  }, [meetingId]);

  useEffect(load, [load]);

  // Build the link from the live origin so it is correct in every environment.
  const publicUrl = state?.token ? `${window.location.origin}/recap/${state.token}` : "";

  async function publish() {
    if (usePasscode && !/^\d{4}$/.test(passcode)) {
      toast.error("The passcode must be exactly 4 digits");
      return;
    }
    setBusy("publish");
    try {
      const { data } = await api.post(`/meetings/${meetingId}/share`, {
        expires_days: expiry,
        passcode: usePasscode ? passcode : null,
        visibility: vis,
      });
      setState(data);
      toast.success(state?.shared ? "Share settings updated" : "Recap link published");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  }

  async function revoke() {
    setBusy("revoke");
    try {
      await api.delete(`/meetings/${meetingId}/share`);
      setState({ shared: false });
      setVis(DEFAULTS);
      setPasscode("");
      setUsePasscode(false);
      toast.success("Link revoked — it no longer opens for anyone");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  }

  function copy() {
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <section data-testid="share-recap" className="yl-surface yl-beam p-6 lg:p-7">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={{ background: "hsl(var(--cyan) / .12)" }}>
          <Share2 className="w-4 h-4" style={{ color: "hsl(var(--ink-info))" }} strokeWidth={2} />
        </div>
        <div className="min-w-0">
          <h3 className="font-heading text-lg sm:text-xl">Shareable recap link</h3>
          <p className="text-xs text-muted-foreground mt-1.5 max-w-xl leading-relaxed">
            Publish a read-only recap for clients and executives who have no account. The raw transcript, email
            addresses and internal metrics are never included — you choose exactly what is visible.
          </p>
        </div>
      </div>

      {state?.shared && (
        <div className="mt-6 rounded-2xl bg-secondary/50 p-4" data-testid="share-live-panel">
          <div className="eyebrow text-[9px]">Live link</div>
          <div className="flex flex-wrap items-center gap-2 mt-2.5">
            <code
              data-testid="share-url"
              className="flex-1 min-w-0 truncate font-mono text-xs bg-card border border-border rounded-xl px-3 py-2.5"
            >
              {publicUrl}
            </code>
            <Button
              type="button"
              data-testid="share-copy-btn"
              onClick={copy}
              variant="outline"
              className="h-10 rounded-xl shrink-0"
            >
              {copied ? <Check className="w-4 h-4" strokeWidth={2.6} /> : <Copy className="w-4 h-4" strokeWidth={2} />}
            </Button>
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="share-open-btn"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-border bg-card text-xs font-bold hover:text-primary hover:border-primary/50 transition-colors shrink-0"
            >
              <Link2 className="w-3.5 h-3.5" strokeWidth={2.2} />
              Open
            </a>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            <span className="inline-flex items-center gap-1.5" data-testid="share-views">
              <Eye className="w-3 h-3" strokeWidth={2} /> {state.views} view{state.views === 1 ? "" : "s"}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Lock className="w-3 h-3" strokeWidth={2} /> {state.has_passcode ? "Passcode on" : "No passcode"}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CalendarClock className="w-3 h-3" strokeWidth={2} />
              {state.expires_at ? `Expires ${new Date(state.expires_at).toLocaleDateString()}` : "No expiry"}
            </span>
          </div>
        </div>
      )}

      {/* ——— settings ——— */}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div>
          <div className="eyebrow text-[9px]">Link expiry</div>
          <div className="flex flex-wrap gap-2 mt-2.5" data-testid="share-expiry-group">
            {EXPIRY.map((e) => (
              <button
                key={String(e.v)}
                type="button"
                data-testid={`share-expiry-${e.v ?? "never"}`}
                onClick={() => setExpiry(e.v)}
                className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${
                  expiry === e.v
                    ? "bg-primary text-primary-foreground border-transparent"
                    : "border-border text-foreground/70 hover:border-primary/50"
                }`}
              >
                {e.l}
              </button>
            ))}
          </div>

          <label className="flex items-center justify-between gap-4 rounded-2xl bg-secondary/50 p-4 mt-4 cursor-pointer">
            <span className="min-w-0">
              <span className="block text-sm font-bold">Require a 4-digit passcode</span>
              <span className="block text-xs text-muted-foreground mt-0.5">
                Share the code separately for client-facing links.
              </span>
            </span>
            <Switch
              data-testid="share-passcode-toggle"
              checked={usePasscode}
              onCheckedChange={setUsePasscode}
            />
          </label>
          {usePasscode && (
            <Input
              data-testid="share-passcode-input"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value.replace(/\D/g, "").slice(0, 4))}
              inputMode="numeric"
              placeholder={state?.has_passcode ? "Enter a new 4-digit code" : "e.g. 4821"}
              className="mt-3 h-11 rounded-xl font-mono tracking-[0.5em] text-center text-lg"
            />
          )}
        </div>

        <div>
          <div className="eyebrow text-[9px]">What outsiders can see</div>
          <ul className="mt-2.5 divide-y divide-border/70">
            {TOGGLES.map((t) => (
              <li key={t.k} className="flex items-center justify-between gap-4 py-3">
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">{t.l}</span>
                  <span className="block text-[11px] text-muted-foreground mt-0.5 leading-snug">{t.d}</span>
                </span>
                <Switch
                  data-testid={`share-vis-${t.k}`}
                  checked={!!vis[t.k]}
                  onCheckedChange={(v) => setVis({ ...vis, [t.k]: v })}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="flex flex-wrap gap-2.5 mt-6">
        <button
          type="button"
          data-testid="share-publish-btn"
          onClick={publish}
          disabled={!!busy}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-primary text-primary-foreground text-sm font-bold shadow-lift hover:bg-[hsl(var(--primary-hover))] transition-colors disabled:opacity-60"
        >
          {busy === "publish" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" strokeWidth={2.2} />}
          {state?.shared ? "Update share settings" : "Publish recap link"}
        </button>
        {state?.shared && (
          <button
            type="button"
            data-testid="share-revoke-btn"
            onClick={revoke}
            disabled={!!busy}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-border bg-card text-sm font-bold text-foreground/80 hover:text-destructive hover:border-destructive/40 transition-colors disabled:opacity-60"
          >
            {busy === "revoke" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" strokeWidth={2} />}
            Revoke link
          </button>
        )}
        {!state?.shared && (
          <span className="inline-flex items-center gap-2 text-xs text-muted-foreground self-center">
            <ShieldOff className="w-3.5 h-3.5" strokeWidth={2} />
            Not shared — nothing is publicly visible
          </span>
        )}
      </div>
    </section>
  );
};

export default ShareRecap;
